import { useRef, type ReactNode } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { Navigate, Outlet, useLocation } from 'react-router';
import { SESSION_POLICY, type Permission } from '@bekebi/domain';
import { ErrorState, LoadingState } from '../components/states';
import { PageFrame } from '../components/page-frame';
import {
  SIGNED_OUT_REASON_KEY,
  useLogout,
  type LoginLocationState,
  type SignedOutReason,
} from './auth-actions';
import { CurrentUserProvider, useCurrentUser, useHasPermission } from './current-user';
import { useIdleTimeout } from './idle';
import { SESSION_QUERY_KEY, useSession } from './session';

/**
 * Route guards. They decide what the browser shows; they are not the security boundary — the API
 * checks the session and the permission of every request and refuses anything not allowed.
 */

/** Re-read the session on the first user activity after this long without any. */
const KEEP_ALIVE_AFTER_MS = 5 * 60_000;

export function SessionCheckError({ message, onRetry }: { message: string; onRetry: () => void }) {
  return (
    <PageFrame>
      <ErrorState title="Your session could not be checked" message={message} onRetry={onRetry} />
    </PageFrame>
  );
}

/**
 * The authenticated area (/app). Requires a session from GET /api/auth/session, provides the
 * signed-in user to everything inside, and runs the idle sign-out for the user's role.
 */
export function AuthenticatedLayout() {
  const session = useSession();
  const location = useLocation();
  const queryClient = useQueryClient();
  const wasAuthenticated = useRef(false);

  if (session.status === 'loading') return <LoadingState label="Checking your session…" />;
  if (session.status === 'error') {
    return <SessionCheckError message={session.error.message} onRetry={session.retry} />;
  }
  if (session.status === 'unauthenticated') {
    const reason =
      queryClient.getQueryData<SignedOutReason>(SIGNED_OUT_REASON_KEY) ??
      (wasAuthenticated.current ? 'expired' : undefined);
    const state: LoginLocationState = reason ? { reason } : { from: location.pathname };
    return <Navigate to="/login" replace state={state} />;
  }

  wasAuthenticated.current = true;
  return (
    <CurrentUserProvider user={session.user}>
      <IdleSignOut />
      <Outlet />
    </CurrentUserProvider>
  );
}

function IdleSignOut() {
  const user = useCurrentUser();
  const { logout } = useLogout();
  const queryClient = useQueryClient();
  useIdleTimeout({
    timeoutMs: SESSION_POLICY[user.role].idleTimeoutMs,
    onIdle: () => void logout('idle'),
    keepAliveAfterMs: KEEP_ALIVE_AFTER_MS,
    onActiveAfterQuiet: () => void queryClient.invalidateQueries({ queryKey: SESSION_QUERY_KEY }),
  });
  return null;
}

/** Shown in place of a screen the session's permissions do not include (inside the shell's main). */
export function AccessDenied() {
  return (
    <div
      role="alert"
      className="max-w-3xl rounded-lg border border-warning-line bg-warning-soft p-6"
    >
      <h1 className="text-base font-semibold text-warning">You do not have access to this page</h1>
      <p className="mt-2 text-sm text-warning">
        Your role does not include this area of the Bekebi Portal.
      </p>
    </div>
  );
}

/**
 * Renders its children (or nested routes) only when the session lists `permission`; otherwise an
 * access-denied state. Use inside the authenticated area. Permission names come from the backend
 * permission map (packages/domain), so none can be invented here.
 */
export function RequirePermission({
  permission,
  children,
}: {
  permission: Permission;
  children?: ReactNode;
}) {
  const allowed = useHasPermission(permission);
  if (!allowed) return <AccessDenied />;
  return children ?? <Outlet />;
}

/**
 * Renders `admin` for the Admin role and `ict` for ICT (internal role `operations`). For pages
 * that share a route but differ by view; access itself is decided by RequirePermission.
 */
export function ByRole({ admin, ict }: { admin: ReactNode; ict: ReactNode }) {
  return <>{useCurrentUser().role === 'admin' ? admin : ict}</>;
}
